import { autoinject } from 'aurelia-framework';
import { DialogController } from 'aurelia-dialog';
import { PenDimensionPipeSegmentService } from 'services/pen/pen-dimension-pipe-segment-service';
import { BulkHeadStatusType, PenDimensionPipeSegment } from 'models/pen/PenDimensionPipeSegment';
import { AsyncErrorHandler } from 'lib/ui';
import { ToastService } from 'services/toast-service';
import { StandardValidationBuilder } from 'lib/validation';
import { ValidationController } from 'aurelia-validation';
import { connectTo } from 'aurelia-store';
import { ApplicationState } from 'lib/state';
import { PenDimensionPipeSegmentStatus } from 'models/pen/PenDimensionPipeSegmentStatus';
import { PubSub } from 'lib/event/PubSub';
import { I18N } from 'aurelia-i18n';
import { AttachmentHandlerFactory } from 'services/attachments/attachment-handler-factory';
import {
  PenDimensionPipeSegmentDocuemntsHandler,
  PenDimensionPipeSegmentAttachmentTypeEnum,
} from 'services/attachments/pen-dimension-pipe-segment-documents-handler';
import { UserService } from 'services/user-service';
import { UserModels } from 'models/UserModels';
import { WeldingMachineService } from 'services/pen/welding-machine-service';

@autoinject
@connectTo()
export class PenSegmentDialog {
  protected state: ApplicationState;
  protected penDimensionPipeSegment: PenDimensionPipeSegment;
  protected colors: any[] = [];
  protected readyForInspection: boolean = false;
  protected isLocked = false;
  protected isSupervisore = false;
  protected isAdmin = false;
  protected headerText: string;
  protected users: UserModels.UserSimple[] = [];
  protected _pipeList;

  protected bulkHeadDocumentHandler: PenDimensionPipeSegmentDocuemntsHandler;
  protected weldDocumentHandler: PenDimensionPipeSegmentDocuemntsHandler;

  constructor(
    private dialogController: DialogController,
    private penDimensionPipeSegmentService: PenDimensionPipeSegmentService,
    private toastService: ToastService,
    private ruleBuilder: StandardValidationBuilder,
    protected validationController: ValidationController,
    private attachmentHandlerFactory: AttachmentHandlerFactory,
    private weldingMachineService: WeldingMachineService,
    protected userService: UserService,
    private pubSub: PubSub,
    private t: I18N
  ) {}

  protected async activate(model: { penDimensionPipeSegmentId: number; pipeList: any }) {
    const response = await this.penDimensionPipeSegmentService.getPenDimensionPipeSegment(model.penDimensionPipeSegmentId);

    this._pipeList = model.pipeList;

    this.penDimensionPipeSegment = response.data;

    if (this.penDimensionPipeSegment.Date === null) {
      this.penDimensionPipeSegment.Date = new Date();
    }

    if (
      this.penDimensionPipeSegment.PenDimensionPipeSegmentStatusId === PenDimensionPipeSegmentStatus.Done ||
      this.penDimensionPipeSegment.PenDimensionPipeSegmentStatusId === PenDimensionPipeSegmentStatus.Approved
    ) {
      this.isLocked = true;
    }
    this.createUpdateValidationRules();
    this.setBarColors();
    this.setBarColors();
    this.setHeaderText();

    this.bulkHeadDocumentHandler = this.attachmentHandlerFactory.penDimensionPipeSegmentHandler({
      penDimensionPipeSegmentId: this.penDimensionPipeSegment.Id,
      penDimensionPipeSegmentAttachmentTypeEnum: PenDimensionPipeSegmentAttachmentTypeEnum.BulkHead,
    });

    this.weldDocumentHandler = this.attachmentHandlerFactory.penDimensionPipeSegmentHandler({
      penDimensionPipeSegmentId: this.penDimensionPipeSegment.Id,
      penDimensionPipeSegmentAttachmentTypeEnum: PenDimensionPipeSegmentAttachmentTypeEnum.Weld,
    });
  }

  protected attached() {
    if (this.state.roles.includes('PensProductionSupervisor')) {
      this.isSupervisore = true;
    }

    if (this.state.roles.includes('PensAdministrator') || this.state.roles.includes('Administrator')) {
      this.isAdmin = true;
    }

    if (this.penDimensionPipeSegment.WelderUserId === null) {
      this.penDimensionPipeSegment.WelderUserId = this.state.user.Id;
    }
  }

  @AsyncErrorHandler
  async getWeldingMachines() {
    const options = await this.weldingMachineService.getAll();
    return options.map((x) => ({
      Id: x.Id,
      Model: `${x.Manufacturer} - ${x.Model} - ${x.MachineNumber}`,
    }));
  }

  @AsyncErrorHandler
  async getPenProductionsUsers() {
    const { data: users } = await this.userService.getPenProductionUsers();
    this.users = users;
    return this.users;
  }

  @AsyncErrorHandler
  async save() {
    await this.penDimensionPipeSegmentService.updatePenDimensionPipeSegment(this.penDimensionPipeSegment.Id, this.penDimensionPipeSegment);
    await this.validateReadyForInspection();
    this.toastService.showSuccess('general.updated');
  }

  @AsyncErrorHandler
  async setPenDimensionPipeSegmentStatus(status: string) {
    const { valid } = await this.validationController.validate();
    if (!valid) {
      this.readyForInspection = false;
      return;
    }
    if (!this.bulkHeadDocumentHandler.hasAttachment && !this.weldDocumentHandler.hasAttachment) return;

    let statusId = 0;
    switch (status) {
      case 'Done':
        statusId = PenDimensionPipeSegmentStatus.Done;
        break;
      case 'Approved':
        statusId = PenDimensionPipeSegmentStatus.Approved;
        break;
      case 'NotApproved':
        statusId = PenDimensionPipeSegmentStatus.NotApproved;
        break;
      default:
        break;
    }

    if (statusId != 0) {
      await this.penDimensionPipeSegmentService.updatePenDimensionPipeSegmentStatus(this.penDimensionPipeSegment.Id, statusId);
      this.dialogController.close(true);
      this.toastService.showSuccess('Status oppdatert');
    }
  }

  protected onCancel() {
    void this.dialogController.close(true);
  }

  private async createUpdateValidationRules() {
    if (this.penDimensionPipeSegment.BulkHeadStatusType === BulkHeadStatusType.NoBulkHead) {
      this.ruleBuilder
        .with(this.penDimensionPipeSegment)
        .required('WeldNo')
        .required('Date')
        .required('JoiningPressure')
        .required('WeldTemp')
        .required('HeatingTimeWithPressure')
        .required('TotalFusionPressure')
        .required('HeatingTimeWithoutPressure')
        .required('DragPressure')
        .required('PlateTempMeasured')
        .required('ChangeOverTime')
        .required('CoolDownTimeWithPressure')
        .required('BatchNr')
        .done();
    }

    if (
      this.penDimensionPipeSegment.BulkHeadStatusType === BulkHeadStatusType.YesPreWeldedBulkHead ||
      this.penDimensionPipeSegment.BulkHeadStatusType === BulkHeadStatusType.YesExtrusionWeldedBulkHead
    ) {
      this.ruleBuilder
        .with(this.penDimensionPipeSegment)
        .required('WeldNo')
        .required('Date')
        .required('BulkHeadWeldNo')
        .required('JoiningPressure')
        .required('WeldTemp')
        .required('HeatingTimeWithPressure')
        .required('TotalFusionPressure')
        .required('HeatingTimeWithoutPressure')
        .required('DragPressure')
        .required('PlateTempMeasured')
        .required('ChangeOverTime')
        .required('CoolDownTimeWithPressure')
        .required('BatchNr')
        .done();
    }
  }

  protected async validateReadyForInspection() {
    const { valid } = await this.validationController.validate();
    if (!this.bulkHeadDocumentHandler.hasAttachment && !this.weldDocumentHandler.hasAttachment) return;
    this.readyForInspection = valid;
  }

  protected setHeaderText() {
    const segmentNr = this.t.tr('pens.segmentNr');
    const weldSegment = this.t.tr('pens.weldSegment');
    const and = this.t.tr('general.and');

    this.headerText = `${segmentNr} ${this.penDimensionPipeSegment.Index} - ${weldSegment} ${this.penDimensionPipeSegment.Index} ${and} ${this.penDimensionPipeSegment.WeldToPenDimensionPipeSegmentIndex}`;
  }

  protected setBarColors() {
    this.colors = [];

    if (this.penDimensionPipeSegment.Index === this._pipeList.length) {
      for (let i = 1; i <= this._pipeList.length; i++) {
        if (i === 1) {
          const item = this._pipeList.find((item) => item.index === i);
          item.color = '#fff1cc';
          item.arrow = '⬅';
          item.arrowLeft = true;
          item.border = '2px dashed #f6aa27';
        } else if (i === this._pipeList.length) {
          const item = this._pipeList.find((item) => item.index === i);
          item.color = '#fff1cc';
          item.arrow = '➡';
          item.arrowRight = true;
          item.border = '2px dashed #f6aa27';
        } else {
          const item = this._pipeList.find((item) => item.index === i);
          if (item.isDone) {
            item.color = '#e5eef1';
            item.border = '2px solid #4bcf7c';
          }
        }
      }
    } else {
      for (let i = 1; i <= this._pipeList.length; i++) {
        if (i === this.penDimensionPipeSegment.Index) {
          const item = this._pipeList.find((item) => item.index === i);
          item.color = '#fff1cc';
          item.arrow = '➡';
          item.arrowRight = true;
          item.border = '2px dashed #f6aa27';
        } else if (i === this.penDimensionPipeSegment.Index + 1) {
          const item = this._pipeList.find((item) => item.index === i);
          item.color = '#fff1cc';
          item.arrow = '⬅';
          item.arrowLeft = true;
          item.border = '2px dashed #f6aa27';
        } else {
          const item = this._pipeList.find((item) => item.index === i);
          if (item.isDone) {
            item.color = '#e5eef1';
            item.border = '2px solid #4bcf7c';
          }
        }
      }
    }
  }
}
